import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * A custom element that plays the product-card video it wraps as an ambient
 * preview: muted, looping, and only while visible in the viewport.
 *
 * The poster image underneath stays in place until playback starts; visitors
 * who prefer reduced motion keep the static poster.
 *
 * @extends {Component}
 */
class CardVideo extends Component {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();

    const video = this.querySelector('video');
    if (!(video instanceof HTMLVideoElement) || prefersReducedMotion()) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (!entry) return;

        if (entry.isIntersecting) {
          // Videos re-inserted by section morphing can lose the muted
          // property even with the attribute present, which blocks play().
          video.muted = true;
          video
            .play()
            .then(() => this.setAttribute('playing', ''))
            .catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.3 }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
  }
}

if (!customElements.get('card-video')) {
  customElements.define('card-video', CardVideo);
}
